import { useSyncExternalStore } from 'react';

/**
 * Subscribes to a CSS media query.
 *
 * Why a hook rather than Tailwind's `lg:` variants: the desktop layout MOVES the
 * primary action from the bottom bar into the summary rail. Doing that with CSS
 * means rendering it twice and hiding one, which puts two "Continue" buttons in
 * the accessibility tree — a screen-reader user hears the same control twice,
 * and any query for it becomes ambiguous. One node that changes parent is the
 * honest implementation.
 *
 * `useSyncExternalStore` rather than useState+useEffect: it reads the match
 * during render instead of after it, so the first paint is already correct and
 * the action does not visibly jump from the bar to the rail on load.
 *
 * Pure CSS is still right for everything that only changes APPEARANCE — this is
 * only for the handful of places where the DOM itself must differ.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      // matchMedia is missing in some non-browser test environments; treating it
      // as "no match" is the mobile-first answer and cannot crash a render.
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    () => {
      if (typeof window === 'undefined' || !window.matchMedia) return false;
      return window.matchMedia(query).matches;
    },
    // Server snapshot: mobile-first, so no match.
    () => false,
  );
}

/** The one breakpoint at which the customer flow gains its summary rail. */
export const DESKTOP_QUERY = '(min-width: 1024px)';

/** True on a desktop-width viewport. See `useMediaQuery` for why this is JS. */
export function useIsDesktop(): boolean {
  return useMediaQuery(DESKTOP_QUERY);
}
